/* MAYLILY: Japanese autocomplete uses storefront search (the predictive API excludes ja).
   Shared by the header, homepage and search page; debounce, IME and stale requests are handled here. */
(() => {
  const cache = new Map();
  let instance = 0;
  const style = document.createElement('style');
  style.textContent = `
    predictive-search.ml-autocomplete{display:block;position:relative;width:100%}
    .header__search .search-modal__form.ml-autocomplete{margin-inline:auto}
    .ml-autocomplete .common-search{position:relative}
    .ml-autocomplete .predictive-search{position:absolute;top:100%;left:0;width:100%;z-index:30;background:#fff;color:#666464;border:1px solid #ddd5d1;box-shadow:0 4px 12px #0000000d;text-align:left;overflow:auto;max-height:min(42vh,360px)}
    .ml-autocomplete .predictive-search[hidden]{display:none!important}
    .ml-autocomplete [role=listbox]{margin:0;padding:0;list-style:none}
    .ml-autocomplete [role=option]{margin:0;padding:0}
    .ml-autocomplete [role=option]>a{display:flex;align-items:center;gap:12px;padding:10px 14px;min-height:60px;color:inherit;text-decoration:none;line-height:1.5;font-size:14px}
    .ml-autocomplete [role=option]>a:hover,.ml-autocomplete [aria-selected=true]>a{background:#f7f3f1}
    .ml-autocomplete [role=option] img{width:48px;height:48px;object-fit:contain;flex-shrink:0}
    .ml-autocomplete .ml-search-all{border-top:1px solid #eee7e3;font-size:13px}
    .ml-autocomplete .ml-search-empty{padding:14px;font-size:13px}
    /* Keep desktop search and its two link columns on one compact centered axis. */
    @media(min-width:750px){
      .header__search .search-modal__content .title{margin:24px 0}
      .header__search .search-modal__form.ml-autocomplete{max-width:478px}
      .header__search .search-modal__menu{display:grid;grid-template-columns:minmax(0,1fr) minmax(0,1fr);column-gap:32px;width:100%;max-width:478px;margin:24px auto 0;padding:0;box-sizing:border-box}
      .header__search .search-modal__list{min-width:0}
    }
    @media(max-width:749px){
      .ml-autocomplete,.ml-autocomplete form.search,.ml-autocomplete .common-search{width:100%;max-width:100%;min-width:0;box-sizing:border-box}
      .ml-autocomplete input[type=search]{font-size:16px!important;width:100%;max-width:100%;min-width:0;box-sizing:border-box}
      .ml-autocomplete .predictive-search{box-sizing:border-box;width:100%;max-width:100%;overscroll-behavior:contain;-webkit-overflow-scrolling:touch}
      .ml-autocomplete [role=option]>a{font-size:13px;padding:8px 12px;min-height:54px;box-sizing:border-box;min-width:0}
      .ml-autocomplete [role=option]>a span{min-width:0;overflow-wrap:anywhere}
      .ml-autocomplete [role=option] img{width:40px;height:40px}
    }
  `;
  document.head.appendChild(style);
  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.input = this.querySelector('input[type=search]');
      this.form = this.querySelector('form');
      if (!this.input || !this.form) return;
      this.ready = true;
      this.classList.add('ml-autocomplete');
      this.number = ++instance;
      this.sequence = 0;
      this.isOpen = false;
      this.panel = this.querySelector('[data-predictive-search]');
      if (!this.panel) {
        this.panel = document.createElement('div');
        this.panel.className = 'predictive-search';
        this.panel.setAttribute('data-predictive-search', '');
        this.input.closest('.common-search').appendChild(this.panel);
      }
      this.status = this.querySelector('.predictive-search-status');
      if (!this.status) {
        this.status = document.createElement('span');
        this.status.className = 'predictive-search-status visually-hidden';
        this.status.setAttribute('role', 'status');
        this.appendChild(this.status);
      }
      this.input.id = 'MLSearch-' + this.number;
      this.input.setAttribute('aria-label', '商品を検索');
      this.input.setAttribute('role', 'combobox');
      this.input.setAttribute('aria-autocomplete', 'list');
      this.input.setAttribute('aria-haspopup', 'listbox');
      this.input.setAttribute('aria-controls', 'MLSearchResults-' + this.number);
      this.input.removeAttribute('aria-owns');
      this.input.setAttribute('autocomplete', 'off');
      this.close();
      // Follow the visible area when iOS opens its keyboard.
      this.fitVisibleArea = () => {
        if (!this.isOpen) return;
        const viewport = window.visualViewport;
        const visibleBottom = viewport ? viewport.height + viewport.offsetTop : window.innerHeight;
        const available = Math.max(80, visibleBottom - this.panel.getBoundingClientRect().top - 12);
        this.panel.style.maxHeight = Math.min(360, available) + 'px';
      };
      if (window.visualViewport) {
        window.visualViewport.addEventListener('resize', this.fitVisibleArea);
        window.visualViewport.addEventListener('scroll', this.fitVisibleArea);
      }
      // Search intermediate IME text too; only keyboard navigation waits for conversion.
      this.input.addEventListener('compositionstart', () => { this.composing = true; this.cancel(); });
      this.input.addEventListener('compositionend', () => { this.composing = false; this.schedule(); });
      this.input.addEventListener('input', () => this.schedule());
      this.input.addEventListener('focus', () => { if (this.input.value.trim()) this.schedule(); });
      this.addEventListener('focusout', () => setTimeout(() => { if (!this.contains(document.activeElement)) this.close(); }, 150));
      this.input.addEventListener('keydown', event => this.keydown(event));
      this.form.addEventListener('submit', event => { if (!this.input.value.trim()) event.preventDefault(); });
    }
    cancel() { clearTimeout(this.timer); this.sequence++; if (this.controller) this.controller.abort(); this.removeAttribute('loading'); }
    close() {
      this.panel.hidden = true;
      this.isOpen = false;
      this.removeAttribute('open');
      this.input.setAttribute('aria-expanded', 'false');
      this.input.removeAttribute('aria-activedescendant');
    }
    schedule() {
      this.cancel();
      this.close();
      const term = this.input.value.trim();
      if (!term) { this.status.textContent = ''; return; }
      this.timer = setTimeout(() => this.results(term), 300);
    }
    url(term) {
      const url = new URL(this.form.action, location.origin);
      url.searchParams.set('q', term);
      url.searchParams.set('type', 'product');
      url.searchParams.set('options[prefix]', 'last');
      return url;
    }
    async results(term) {
      const ticket = this.sequence;
      try {
        let items = cache.get(term);
        if (!items) {
          this.controller = new AbortController();
          this.setAttribute('loading', '');
          this.status.textContent = '商品候補を検索中';
          const response = await fetch(this.url(term), { signal: this.controller.signal });
          if (!response.ok) throw new Error('Search unavailable');
          const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
          items = Array.from(doc.querySelectorAll('#product-grid .grid__item')).map(card => {
            const a = card.querySelector('.card__heading a');
            const image = card.querySelector('.card__media img');
            return a ? { title: a.textContent.trim(), href: a.getAttribute('href'), image: image && image.getAttribute('src') } : null;
          }).filter(Boolean).slice(0, 4);
          if (cache.size >= 20) cache.delete(cache.keys().next().value);
          cache.set(term, items);
        }
        if (ticket !== this.sequence || term !== this.input.value.trim() || !this.contains(document.activeElement)) return;
        this.render(items, term);
      } catch (error) {
        if (ticket === this.sequence) { this.close(); this.status.textContent = ''; }
      } finally { if (ticket === this.sequence) this.removeAttribute('loading'); }
    }
    render(items, term) {
      this.panel.replaceChildren();
      const list = document.createElement('ul');
      list.id = 'MLSearchResults-' + this.number;
      list.setAttribute('role', 'listbox');
      list.setAttribute('aria-label', '商品候補');
      const add = (title, href, image, all = false) => {
        const li = document.createElement('li');
        li.id = list.id + '-' + list.children.length;
        li.setAttribute('role', 'option');
        li.setAttribute('aria-selected', 'false');
        const a = document.createElement('a');
        a.href = href;
        if (all) a.className = 'ml-search-all';
        if (image) { const img = document.createElement('img'); img.src = image; img.alt = ''; img.width = 48; img.height = 48; a.appendChild(img); }
        const label = document.createElement('span');
        label.textContent = title;
        a.appendChild(label);
        li.appendChild(a);
        list.appendChild(li);
      };
      items.forEach(item => add(item.title, item.href, item.image));
      if (!items.length) {
        const empty = document.createElement('p');
        empty.className = 'ml-search-empty';
        empty.textContent = '該当する商品が見つかりませんでした';
        this.panel.appendChild(empty);
      }
      add('「' + term + '」の検索結果をすべて表示', this.url(term).href, null, true);
      this.panel.appendChild(list);
      this.panel.hidden = false;
      this.isOpen = true;
      this.setAttribute('open', '');
      this.input.setAttribute('aria-expanded', 'true');
      this.status.textContent = items.length + '件の商品候補';
      this.fitVisibleArea();
    }
    keydown(event) {
      if (event.isComposing || this.composing) return;
      if (event.key === 'Escape' && this.isOpen) { event.preventDefault(); event.stopPropagation(); this.cancel(); this.close(); return; }
      if (!this.isOpen) return;
      const options = Array.from(this.panel.querySelectorAll('[role=option]'));
      const selected = options.findIndex(x => x.getAttribute('aria-selected') === 'true');
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        const index = event.key === 'ArrowDown' ? (selected + 1) % options.length : (selected < 0 ? options.length - 1 : (selected - 1 + options.length) % options.length);
        options.forEach((x, i) => x.setAttribute('aria-selected', String(i === index)));
        this.input.setAttribute('aria-activedescendant', options[index].id);
        options[index].scrollIntoView({ block: 'nearest' });
      } else if (event.key === 'Enter' && selected >= 0) { event.preventDefault(); options[selected].querySelector('a').click(); }
    }
  }
  // The homepage uses the same form without the custom element.
  document.querySelectorAll('.top-search__box form.search').forEach(form => {
    if (form.closest('predictive-search')) return;
    const wrapper = document.createElement('predictive-search');
    form.before(wrapper);
    wrapper.appendChild(form);
  });
  if (!customElements.get('predictive-search')) customElements.define('predictive-search', PredictiveSearch);
})();
